/**
 * usePromoCodeCapture — claim `?promo=CODE` from a tenant-distributed link.
 *
 * D21: the capture is SESSION-scoped. The code is stashed in `sessionStorage`
 * (per-tab, gone on tab close) so it survives a refresh and the funnel's
 * step changes; a fresh `?promo=` always wins over a stashed one (last
 * capture wins). Unlike the unlock token, the URL is NOT scrubbed on claim —
 * a promo code is marketing content, not a credential, and leaving it keeps
 * the link shareable and refresh-safe even where storage is blocked. The one
 * exception: once the member OVERRIDES the link (types a different code, or
 * removes it), the param is dropped — otherwise the next refresh re-claims
 * the URL code and silently undoes their choice.
 *
 * Raw `sessionStorage` (not `browserAdapter.session`) on purpose: the claim
 * must run in a `useState` initializer — before the first render — and the
 * adapter API is async. Same call and same try/catch posture as the two
 * in-flow precedents (`useUnlockLink.claimToken`, PaymentContext's guest
 * email); in a storage-blocked iframe the URL param still carries the code
 * for this page view.
 *
 * One instance PER FUNNEL, each on its own storage key: the subscription
 * funnel (`SubscriptionPurchaseProvider`, read via
 * `useSubscriptionPurchase().promoCode`) and the booking flow
 * (`BookingStepProvider`, read via `useBookingStep().promoCode`). Separate keys
 * keep a code captured for a plan out of a booking quote and vice versa; a
 * second instance on the SAME key would race the URL-vs-storage precedence.
 * Each funnel's provider mounts only on its own route, so a `?promo=` is
 * claimed only by the funnel the link was built for.
 *
 * @module features/promotions/usePromoCodeCapture
 */

import { useCallback, useState } from 'react';
import { QueryParams } from '~/lib/queryParams';

// `butter.` dot-namespace — the sibling unlock hook's shape (`butter.unlockToken`).
/** The subscription funnel's key (the original, pre-booking-links name). */
export const SUBSCRIPTION_PROMO_KEY = 'butter.promoCode';
/** The booking flow's key. */
export const BOOKING_PROMO_KEY = 'butter.bookingPromoCode';

/**
 * Forget a funnel's stashed code outside the hook — for a page that runs after
 * the funnel has unmounted (the booking confirmation page), where there is no
 * hook instance to call `clearPromoCode` on.
 */
export function forgetPromoCode(storageKey: string): void {
  try {
    sessionStorage.removeItem(storageKey);
  } catch {
    // Nothing stashed to remove where storage is blocked.
  }
}

function claimPromoCode(storageKey: string, urlCode: string | null): string | null {
  const fromUrl = urlCode?.trim();
  if (fromUrl) {
    try {
      sessionStorage.setItem(storageKey, fromUrl);
    } catch {
      // Private browsing / blocked storage: the capture still works for this
      // page view; only refresh persistence is lost (the URL usually has it).
    }
    return fromUrl;
  }
  try {
    return sessionStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

export interface PromoCodeCapture {
  /** The captured code, or null when none was ever captured (or it was cleared). */
  promoCode: string | null;
  /**
   * Capture a MANUALLY-ENTERED code (the checkout steps' input field, D21
   * amendment) — same stash + state as a link capture, so it survives a
   * refresh identically. Trimmed; blank routes to `clearPromoCode` rather
   * than stashing ''.
   */
  setPromoCode: (code: string) => void;
  /**
   * Forget the code — storage and state. Called on successful purchase, on a
   * server-side `promotion_rejected` (the auto-drop: requote at list price),
   * and by the input field's remove affordance.
   */
  clearPromoCode: () => void;
}

export function usePromoCodeCapture(storageKey: string): PromoCodeCapture {
  // Claimed before the first render (not an effect) so the first quote request
  // already carries the code — no flash of the undiscounted price.
  //
  // Read from the REAL URL, not the router's `useSearchParams`: the scrub below
  // bypasses the router, so after an override the router's location still
  // holds the link's code. A provider that REMOUNTS under the same router (the
  // booking flow does, when WaiverGate swaps it out for the waiver screen)
  // would re-claim that stale code over the member's own choice.
  const [promoCode, setPromoCode] = useState<string | null>(() =>
    claimPromoCode(
      storageKey,
      new URLSearchParams(window.location.search).get(QueryParams.PROMO),
    ),
  );

  // The member overrode the link's code — scrub the param so a refresh doesn't
  // re-claim it over their choice. Raw `history.replaceState` (the
  // `useUnlockLink` precedent), NOT a router write (`setSearchParams`, or
  // `useTenantSearchParams` from `~/lib/navigation`): `clearPromoCode` runs
  // right after the purchase's `navigate('/…/confirmed/…', { state })`, before
  // the data router commits it. A router write from here resolves against the
  // funnel's stale location and replaces that entry — the confirmation page
  // loses its `location.state` and reads "Subscription Not Found".
  // `window.history.state` is passed back through so the router's own state
  // survives untouched.
  // Known and fine: react-router's `location.search` stays stale after a raw
  // replaceState. The claim reads `window.location` for exactly that reason, and
  // nothing may copy the router's params onward wholesale (the booking steps
  // build their confirmation URL from the preserved allow-list) — do not "fix"
  // this by switching to setSearchParams.
  const scrubPromoParam = useCallback(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(QueryParams.PROMO)) return;
    url.searchParams.delete(QueryParams.PROMO);
    window.history.replaceState(window.history.state, '', url);
  }, []);

  const clearPromoCode = useCallback(() => {
    forgetPromoCode(storageKey);
    setPromoCode(null);
    scrubPromoParam();
  }, [storageKey, scrubPromoParam]);

  const setPromoCodeManually = useCallback(
    (code: string) => {
      const trimmed = code.trim();
      if (!trimmed) {
        clearPromoCode();
        return;
      }
      try {
        sessionStorage.setItem(storageKey, trimmed);
      } catch {
        // Same posture as the link claim: state still carries it this view.
      }
      setPromoCode(trimmed);
      scrubPromoParam();
    },
    [storageKey, clearPromoCode, scrubPromoParam],
  );

  return { promoCode, setPromoCode: setPromoCodeManually, clearPromoCode };
}
